import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import http from '@/services/http';

export type ScopedAsyncOperationPendingPayload = {
    pending: boolean;
    operation_id?: string;
    type?: string;
    /** queued | processing | succeeded | failed — terminal values appear with pending=false (see backend). */
    status?: string;
    error_code?: string | null;
    meta?: Record<string, unknown>;
};

/** Fixed query-key slice for invalidateQueries outside component scope (Echo completion, polling). */
export function scopedPendingStaticQueryKey(
    operationType: string,
    scopeUpper: string
): readonly ['async-operations', 'pending', string, string] {
    return ['async-operations', 'pending', operationType.trim(), scopeUpper.trim().toUpperCase()] as const;
}

/** Vue-query key for scoped pending lookups (invalidate when an operation completes). */
export function pendingScopedAsyncOperationQueryKey(
    operationType: string,
    scopeUpper: ComputedRef<string>
): ComputedRef<ReturnType<typeof scopedPendingStaticQueryKey>> {
    return computed(() => scopedPendingStaticQueryKey(operationType, scopeUpper.value));
}

async function fetchScopedPendingAsyncOperation(
    operationType: string,
    scopeUpper: string
): Promise<ScopedAsyncOperationPendingPayload> {
    const trimmedType = operationType.trim();

    const { data } = await http.get<{ data: ScopedAsyncOperationPendingPayload }>(
        '/api/async-operations/pending',
        {
            params: {
                type: trimmedType,
                scope: scopeUpper.trim(),
            },
        }
    );

    return data.data;
}

/**
 * Poll Redis-backed scoped async-operation state so the UI survives refresh before Echo reconnects,
 * or when realtime delivery is flaky. Any feature can reuse this by passing type + uppercase scope suffix.
 */
export function useScopedPendingAsyncOperation(
    operationType: string,
    scopeUpper: ComputedRef<string>,
    enabled: MaybeRefOrGetter<boolean> = true
) {
    const queryKey = pendingScopedAsyncOperationQueryKey(operationType, scopeUpper);

    return useQuery({
        queryKey,
        enabled: computed(() => toValue(enabled) && scopeUpper.value.trim() !== ''),
        queryFn: () => fetchScopedPendingAsyncOperation(operationType, scopeUpper.value),
        refetchInterval: (query): number | false =>
            query.state.data?.pending === true ? 6_000 : false,
        staleTime: 0,
    });
}
